import { type MouseEvent, type TouchEvent, useCallback, useEffect, useId, useRef, useState } from 'react';

import { useMediaQuery } from '@hrworks/design-system/hooks';
import { useFloat } from '@hrworks/sui-shared/components';
import { detectOS, mergeRefs } from '@hrworks/sui-shared/functions';

import { Portal } from '../Portal';
import { Text } from '../Text';
import { Title } from '../Title';
import { S } from './Tooltip.styles';
import type { TooltipProps } from './Tooltip.types';

const LONG_INTERACTION_DURATION = 500;
const SKIP_DELAY_WINDOW = 300;

export const Tooltip = ({
  children,
  placement = 'top',
  htmlTag: Element = 'div',
  fullWidth,
  text,
  title,
  mainAxisOffset = 7,
  customTooltip,
  unstyledTrigger,
  trigger = 'longHoverOrLongTouch',
  ...otherProps
}: TooltipProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [skipAnimation, setSkipAnimation] = useState(false);
  const touchStartTimeRef = useRef<number | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastClosedRef = useRef<number>(0);
  const tooltipId = useId();
  const isTouchDevice = useMediaQuery('isTouchDevice');

  const clearTimer = useCallback(
    (event?: TouchEvent | MouseEvent) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      } else if (trigger === 'longHoverOrLongTouch') {
        event?.preventDefault();
      }
    },
    [trigger],
  );

  const onTooltipEnter = useCallback(() => {
    if (trigger === 'longHover' && isTouchDevice) {
      return;
    }

    const showTooltipWithDelay = () => {
      const timeSinceLastClosed = Date.now() - lastClosedRef.current;
      if (timeSinceLastClosed < SKIP_DELAY_WINDOW) {
        setSkipAnimation(true);
        setShowTooltip(true);

        return;
      }
      setSkipAnimation(false);
      timerRef.current = setTimeout(() => {
        setShowTooltip(true);
        clearTimer();
      }, LONG_INTERACTION_DURATION);
    };

    switch (trigger) {
      case 'hoverOrTouch':
        setShowTooltip(true);
        break;
      case 'longHoverOrLongTouch':
        if (isTouchDevice) {
          touchStartTimeRef.current = Date.now();
        }
        showTooltipWithDelay();
        break;
      default:
        showTooltipWithDelay();
    }
  }, [clearTimer, isTouchDevice, trigger]);

  const onTooltipLeave = useCallback(
    (event: TouchEvent | MouseEvent) => {
      clearTimer(event);

      const handleTouchDevice = () => {
        if (trigger === 'longHoverOrLongTouch') {
          const touchDuration = touchStartTimeRef.current ? Date.now() - touchStartTimeRef.current : 0;
          if (touchDuration < LONG_INTERACTION_DURATION) {
            setShowTooltip(false);
          }
        }
      };

      const handleNonTouchDevice = () => {
        setShowTooltip(false);
        lastClosedRef.current = Date.now();
      };

      isTouchDevice ? handleTouchDevice() : handleNonTouchDevice();

      touchStartTimeRef.current = null;
    },
    [clearTimer, isTouchDevice, trigger],
  );

  const handleClickOutside = (event: MouseEvent | FocusEvent | UIEvent) => {
    if (!triggerRef.current?.contains(event.target as HTMLElement)) {
      setShowTooltip(false);
    }
  };

  useEffect(() => {
    document.addEventListener('touchstart', handleClickOutside, { capture: true });

    return () => {
      document.removeEventListener('touchstart', handleClickOutside, { capture: true });
    };
  }, []);

  const onTooltipDismiss = useCallback(() => {
    clearTimer();
    setShowTooltip(false);
    lastClosedRef.current = Date.now();
  }, [clearTimer]);

  useEffect(() => {
    if (!showTooltip) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onTooltipDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showTooltip, onTooltipDismiss]);

  const {
    refs,
    floatingStyles,
    placement: actualPlacement,
  } = useFloat({
    show: showTooltip,
    placement,
    flip: true,
    shift: true,
    mainAxisOffset,
  });

  const { maxWidth, maxHeight, ...filteredStyles } = floatingStyles;

  // On iOS numbers were interpreted as phone numbers and added an underline and broke the layout. This prevents this behaviour. See: FE-1835
  const avoidNumberAutoDetectionOnSafari = (text: string) => {
    if (detectOS() !== 'iOS' || !text) {
      return text;
    }

    return text.replaceAll(/(\d)/g, '$1\u200B');
  };

  return (
    <>
      <S.Trigger
        onTouchStart={onTooltipEnter}
        onTouchEnd={onTooltipLeave}
        onFocus={onTooltipEnter}
        onBlur={onTooltipDismiss}
        fullWidth={fullWidth}
        unstyledTrigger={unstyledTrigger}
        as={Element}
        ref={mergeRefs(refs.setReference, triggerRef)}
        {...(!isTouchDevice && { onMouseEnter: onTooltipEnter, onMouseLeave: onTooltipLeave })}
        {...(showTooltip && { 'aria-describedby': tooltipId })}
        {...otherProps}
      >
        {children}
      </S.Trigger>
      {showTooltip && (
        <Portal>
          <S.TooltipContainer
            playAnimation={showTooltip && !skipAnimation}
            placement={actualPlacement}
            id={tooltipId}
            role="tooltip"
            style={{ ...filteredStyles }}
            ref={refs.setFloating}
            isCustom={!!customTooltip}
          >
            {customTooltip || (
              <>
                {title && <Title>{avoidNumberAutoDetectionOnSafari(title)}</Title>}
                {text && (
                  <Text typography="bodySm">
                    {typeof text === 'string' ? avoidNumberAutoDetectionOnSafari(text) : text}
                  </Text>
                )}
              </>
            )}
          </S.TooltipContainer>
        </Portal>
      )}
    </>
  );
};
